import type { FormModel } from '@ikegps/core-shared';
import { Input } from '../../../components/input/input';
import { T, Translate } from '../../../i18n';
import type { MithrilVNode } from '../../../mithril';
import { m } from '../../../mithril';
import { Services } from '../../../services';
import { AppStore } from '../../../store';
import { UserCard, type UserCardCtx } from '../../user.assign.modal/user.card';
import { FormDesignerPalette } from '../form.palette';
import { FormFieldEditor } from './form.field.editor';
import { FormFieldTreeView } from './form.field.tree.view';
import { CSS } from './form.info.panel.style';

const USER_CARD_CTX: UserCardCtx = { readonly: true };

export class FormFieldEditorForm extends FormFieldEditor<FormModel> {
	override getTitleState() {
		const displayState = super.getTitleState();

		// Sync with form's current value only when not focused (handles external changes like clearAllFields)
		if (!displayState.focused && displayState.value !== this.field.displayName) {
			displayState.setValue(this.field.displayName);
		}

		return displayState;
	}

	getDescriptionState() {
		let displayState = this.state.get('description');
		if (displayState == null) {
			displayState = this.state.child(T.FORM_FIELD_DESCRIPTION, 'description');
			displayState.isLabelShown = true;
			displayState.isRequired = true;
			displayState.onValue((val) => this.field.Mutator.description(val ?? ''));
		}

		// Sync with form's current value only when not focused (handles external changes like clearAllFields)
		if (!displayState.focused && displayState.value !== this.field.description) {
			displayState.setValue(this.field.description);
		}

		displayState.readOnly = this.field.isLocked || !this.designer.isEditing;
		return displayState;
	}

	override viewTree(): MithrilVNode {
		return FormFieldTreeView.viewForm(this);
	}

	override viewInfo() {
		return m('div', { className: CSS.container }, [
			Input.view(this.getTitleState()),
			Input.view(this.getDescriptionState()),
			m('div', { className: CSS.section.container }, [
				this.field.createdAt != null
					? this.viewOption(T.FORM_CREATED_AT, Services.DateTime.formatDateTime(this.field.createdAt))
					: null,
				this.field.updatedAt != null
					? this.viewOption(T.COLLECTION_UPDATED_AT, Services.DateTime.formatDateTime(this.field.updatedAt))
					: null,
				this.viewPLASupport(),
				this.viewUserInfo(),
			]),
			FormDesignerPalette.view((type) => this.designer.createField(this.field, type), this.depth),
		]);
	}

	private viewUserInfo() {
		if (this.field.updatedBy == null) {
			return null;
		}

		const updatedBy = AppStore.UserProfile.get(this.field.updatedBy);
		if (updatedBy == null) {
			return null;
		}

		return this.viewOption(T.COLLECTION_UPDATED_BY, UserCard.viewChip(updatedBy, USER_CARD_CTX));
	}

	override get isCloneable() {
		return false;
	}

	private viewPLASupport(): MithrilVNode {
		const options = this.field.metadata && this.field.metadata.options;
		if (options == null) {
			return null;
		}

		if (options.spidacalc != null) {
			return this.viewOption(T.FORM_PLA_SUPPORT, Translate(T.PLA_SPIDACALC));
		}

		if (options.ocalc != null) {
			return this.viewOption(T.FORM_PLA_SUPPORT, Translate(T.PLA_OCALC));
		}

		if (options.dds != null) {
			return this.viewOption(T.FORM_PLA_SUPPORT, Translate(T.PLA_DDS));
		}

		if (options.poleforeman != null) {
			return this.viewOption(T.FORM_PLA_SUPPORT, Translate(T.PLA_POLEFOREMAN));
		}

		if (options.ikePoleForeman != null) {
			return this.viewOption(T.FORM_PLA_SUPPORT, Translate(T.PLA_IKE_POLEFOREMAN));
		}

		return null;
	}
}
